import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Flag, Gavel, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API, EmptyBox, ErrorBox, LoadingBox, TabHeader, asList, fmtCell, labelFor } from './AdminShared';

const SKIP_KEYS = new Set(['report_id', 'appeal_id', 'id']);

const KvRows = ({ item, fields }) => (
  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
    {Object.entries(item)
      .filter(([k]) => !SKIP_KEYS.has(k))
      .map(([k, v]) => (
        <li key={k} className="flex items-baseline justify-between gap-3 text-sm border-b border-white/5 pb-2">
          <span className="text-slate-500 text-xs uppercase tracking-wide">{labelFor(k, fields)}</span>
          <span className="text-slate-200 text-right break-all">{fmtCell(k, v)}</span>
        </li>
      ))}
  </ul>
);

export const AdminReports = ({ authHeaders, tx }) => {
  const [reports, setReports] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actions, setActions] = useState({});
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [rres, ares] = await Promise.all([
        fetch(`${API}/api/admin/app-reports?status=pending&limit=100`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/app-appeals?status=pending`, { headers: authHeaders() }),
      ]);
      if (!rres.ok || !ares.ok) throw new Error('reports');
      setReports(asList(await rres.json(), ['reports', 'items']));
      setAppeals(asList(await ares.json(), ['appeals', 'items']));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const reportAction = async (item) => {
    const id = item.report_id || item.id;
    const action = actions[id] || 'dismiss';
    if (!window.confirm(tx.reports.submitConfirm)) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/app-reports/${id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ action, note: (notes[id] || '').trim() }),
      });
      if (!res.ok) throw new Error('action');
      toast.success(tx.reports.done);
      setReports((prev) => prev.filter((x) => (x.report_id || x.id) !== id));
    } catch {
      toast.error(tx.reports.fail);
    } finally {
      setBusy(null);
    }
  };

  const appealDecide = async (item, action) => {
    const id = item.appeal_id || item.id;
    const confirmText = action === 'accept' ? tx.reports.acceptConfirm : tx.reports.rejectConfirm;
    if (!window.confirm(confirmText)) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/app-appeals/${id}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error('decide');
      toast.success(tx.reports.done);
      setAppeals((prev) => prev.filter((x) => (x.appeal_id || x.id) !== id));
    } catch {
      toast.error(tx.reports.fail);
    } finally {
      setBusy(null);
    }
  };

  const actionOptions = [
    ['dismiss', tx.reports.actDismiss],
    ['warn', tx.reports.actWarn],
    ['remove_content', tx.reports.actRemove],
    ['ban', tx.reports.actBan],
  ];

  return (
    <section data-testid="app-reports-section">
      <TabHeader icon={Flag} title={tx.reports.title} onRefresh={load} refreshLabel={tx.common.refresh} testid="app-reports" />

      <div className="mt-6">
        {loading && <LoadingBox text={tx.common.loading} testid="app-reports-loading" />}
        {!loading && error && (
          <ErrorBox text={tx.common.unavailable} retryText={tx.common.retry} onRetry={load} testid="app-reports-error" />
        )}
        {!loading && !error && (
          <div className="space-y-10">
            <div>
              <h3 className="font-heading text-lg font-extrabold text-white mb-4">
                {tx.reports.reportsTitle}
                <span className="ml-2 text-sm font-bold text-krug-goldbright">({reports.length})</span>
              </h3>
              {reports.length === 0 && <EmptyBox text={tx.reports.emptyReports} testid="app-reports-empty" />}
              <div className="space-y-4">
                {reports.map((item) => {
                  const id = item.report_id || item.id;
                  return (
                    <article key={id} data-testid={`report-${id}`} className="rounded-2xl glass-dark p-6">
                      <KvRows item={item} fields={tx.fields} />
                      <div className="mt-5 flex flex-wrap items-end gap-3">
                        <select
                          data-testid={`report-action-${id}`}
                          value={actions[id] || 'dismiss'}
                          onChange={(e) => setActions((prev) => ({ ...prev, [id]: e.target.value }))}
                          className="rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-xs font-bold text-slate-300 outline-none cursor-pointer"
                        >
                          {actionOptions.map(([value, label]) => (
                            <option key={value} value={value} className="bg-krug-navy">
                              {label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          data-testid={`report-note-${id}`}
                          placeholder={tx.reports.notePlaceholder}
                          value={notes[id] || ''}
                          onChange={(e) => setNotes((prev) => ({ ...prev, [id]: e.target.value }))}
                          className="flex-1 min-w-[200px] rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright transition-colors duration-300"
                        />
                        <button
                          type="button"
                          data-testid={`report-submit-${id}`}
                          disabled={busy === id}
                          onClick={() => reportAction(item)}
                          className="inline-flex items-center gap-2 rounded-full bg-krug-gold px-5 py-2.5 text-xs font-bold text-krug-navy hover:bg-krug-goldbright disabled:opacity-50 transition-colors duration-300"
                        >
                          <Gavel size={14} />
                          {tx.reports.submit}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-extrabold text-white mb-4">
                {tx.reports.appealsTitle}
                <span className="ml-2 text-sm font-bold text-krug-goldbright">({appeals.length})</span>
              </h3>
              {appeals.length === 0 && <EmptyBox text={tx.reports.emptyAppeals} testid="app-appeals-empty" />}
              <div className="space-y-4">
                {appeals.map((item) => {
                  const id = item.appeal_id || item.id;
                  return (
                    <article key={id} data-testid={`appeal-${id}`} className="rounded-2xl glass-dark p-6">
                      <KvRows item={item} fields={tx.fields} />
                      <div className="mt-5 flex flex-wrap gap-3">
                        <button
                          type="button"
                          data-testid={`appeal-accept-${id}`}
                          disabled={busy === id}
                          onClick={() => appealDecide(item, 'accept')}
                          className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-5 py-2.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors duration-300"
                        >
                          <CheckCircle2 size={14} />
                          {tx.reports.accept}
                        </button>
                        <button
                          type="button"
                          data-testid={`appeal-reject-${id}`}
                          disabled={busy === id}
                          onClick={() => appealDecide(item, 'reject')}
                          className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-5 py-2.5 text-xs font-bold text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors duration-300"
                        >
                          <XCircle size={14} />
                          {tx.reports.rejectAppeal}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminReports;
